import {
  getAddToFavoritesAmplitudePayload,
  getAddToFavoritesGAOptions,
  getAddToFavoritesProductList,
} from '~/composables/useAddToFavoritesAnalytics'
import { favoritesQueries } from '~/services/queries'

export const useFavorites = () => {
  const state = useState('favorites', () => ({
    type: 'LOT',
    items: [] as Record<string, unknown>[],
    isLoading: false,
  }))

  const type = computed(() => state.value.type)
  const items = computed(() => state.value.items)
  const isLoading = computed(() => state.value.isLoading)

  const setType = (value: string) => {
    state.value.type = value
  }

  const setItems = (favorites: Record<string, unknown>[]) => {
    state.value.items = favorites
  }

  const startLoading = () => {
    state.value.isLoading = true
  }

  const finishLoading = () => {
    state.value.isLoading = false
  }

  const getLotForFavorites = (lotId: number | string, source: string) => {
    if (source === 'CART') {
      return useCart().getLotById(lotId)?.lot
    }
    if (source === 'LOT') {
      const { lot } = useLot(lotId)
      return lot.value
    }
    return null
  }

  const fetchFavoritsLots = async ({ type: favoritesType }: { type: string }) => {
    const { $gql } = useNuxtApp()
    startLoading()
    setItems([])
    const { result } = await $gql.request(favoritesQueries.fetchFavoritsLots, {
      type: favoritesType,
    })
    setItems(result.favorits ?? [])
    finishLoading()
  }

  const favoritLotAdd = async ({ lotId, source }: { lotId: number | string, source: string }) => {
    const { $gql, $tracking } = useNuxtApp()
    startLoading()
    useStoreFields().clearBackendErrors()
    const { result } = await $gql.request(favoritesQueries.favoritLotAdd, {
      lotId: Number(lotId),
    })
    finishLoading()

    if (result.errors.length === 0) {
      const lot = getLotForFavorites(lotId, source)

      if (lot?.id && lot?.category?.id && lot?.prices) {
        const productList = getAddToFavoritesProductList(source, {})
        $tracking.logEventAmplitude(
          'add to favorites',
          getAddToFavoritesAmplitudePayload({ lot, productList }),
        )
        $tracking.logEventGA(
          'add_to_wishlist',
          getAddToFavoritesGAOptions({ lot, productList }),
        )
      }

      return true
    }
    return false
  }

  const favoritLotRemove = async (lotId: number | string) => {
    const { $gql } = useNuxtApp()
    startLoading()
    useStoreFields().clearBackendErrors()
    const { result } = await $gql.request(favoritesQueries.favoritLotRemove, {
      lotId: Number(lotId),
    })
    finishLoading()
    return result.errors.length === 0
  }

  const favoritUserAdd = async (userId: number | string) => {
    const { $gql } = useNuxtApp()
    startLoading()
    useStoreFields().clearBackendErrors()
    const { result } = await $gql.request(favoritesQueries.favoritUserAdd, { userId })
    finishLoading()
    return result.errors.length === 0
  }

  const favoritUserRemove = async (userId: number | string) => {
    const { $gql } = useNuxtApp()
    startLoading()
    useStoreFields().clearBackendErrors()
    const { result } = await $gql.request(favoritesQueries.favoritUserRemove, { userId })
    finishLoading()
    return result.errors.length === 0
  }

  return {
    type,
    items,
    isLoading,
    setType,
    setItems,
    fetchFavoritsLots,
    favoritLotAdd,
    favoritLotRemove,
    favoritUserAdd,
    favoritUserRemove,
  }
}
